/**
 * Okta device token for New Device Behavior Detection.
 *
 * Okta treats each login as a new device when no stable deviceToken is sent in
 * the authn context. Persist a 32-char ID in localStorage and reuse it.
 *
 * @see https://developer.okta.com/docs/reference/api/authn/#context-object
 */

const STORAGE_KEY = "okta_device_token";
const DEVICE_TOKEN_LENGTH = 32;

function generateUuidV4Hex(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

function generateDeviceToken(): string {
  if (typeof crypto === "undefined") {
    throw new TypeError("crypto is not available");
  }

  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }

  return generateUuidV4Hex();
}

/**
 * Returns a stable device token for this browser, creating one if needed.
 */
export function getOrCreateDeviceToken(): string {
  if (typeof window === "undefined" || !window.localStorage) {
    return generateDeviceToken();
  }

  const existing = localStorage.getItem(STORAGE_KEY);
  if (existing && existing.length === DEVICE_TOKEN_LENGTH) {
    return existing;
  }

  const token = generateDeviceToken();
  localStorage.setItem(STORAGE_KEY, token);
  return token;
}

export function getDeviceTokenContext(): { deviceToken: string } {
  return { deviceToken: getOrCreateDeviceToken() };
}
